import React, { useState, useEffect, useCallback, useMemo } from "react";
import { securityAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import {
  RefreshCw, Search, Download, LogOut, Truck, Package, UserCheck,
  AlertTriangle, Clock, X, Edit, Trash2, ChevronDown, ChevronRight, Image
} from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { fmtDateTime, fmtDateTimeLong, fmtTime } from "../utils/dateUtils";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

// ─── FILTER TABS ──────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: "",                    label: "All Entries",         icon: null,        color: "text-[#5C5147]" },
  { id: "food_distribution",   label: "Food Distribution",   icon: Truck,       color: "text-blue-600" },
  { id: "stores",              label: "Stores",              icon: Package,     color: "text-[#637C64]" },
  { id: "visitor",             label: "Visitor Entry / Exit", icon: UserCheck,  color: "text-purple-600" },
  { id: "others",              label: "Others",              icon: AlertTriangle, color: "text-[#D98032]" },
];

const CAT_STYLE = {
  food_distribution: "bg-blue-100 text-blue-700",
  stores:            "bg-[#EDF2EE] text-[#637C64]",
  visitor:           "bg-purple-100 text-purple-700",
  others:            "bg-[#FDF4EB] text-[#D98032]",
};

const DIR_STYLE = {
  inward:  "bg-[#EDF2EE] text-[#637C64]",
  outward: "bg-[#FDF4EB] text-[#D98032]",
};

export default function GateLog() {
  const { kitchens, selectedKitchen } = useKitchen();
  const { user } = useAuth();

  // Route Coordinators and others without security_create only see Food Distribution
  const isAdmin     = user?.is_admin;
  const canCreate   = isAdmin || !!user?.permissions?.security_create;
  const foodDistOnly = !canCreate;  // only see food_distribution entries

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  // Force food_distribution filter for non-security-create users
  const [activeCategory, setActiveCategory] = useState(foodDistOnly ? "food_distribution" : "");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [exitLoading, setExitLoading] = useState(null);
  const [exitDone, setExitDone] = useState({});
  const [error, setError] = useState("");

  // Drill-down, edit, delete state
  const [expandedId, setExpandedId] = useState(null);
  const [fullEntry, setFullEntry] = useState(null);
  const [fullLoading, setFullLoading] = useState(false);
  const [editModal, setEditModal] = useState(null);    // entry being edited
  const [editForm, setEditForm] = useState({});
  const [editReason, setEditReason] = useState("");
  const [deleteModal, setDeleteModal] = useState(null); // entry to delete
  const [deleteReason, setDeleteReason] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  // Lightbox for photos
  const [lightbox, setLightbox] = useState(null); // {url, label}

  const toggleExpand = async (entry) => {
    if (expandedId === entry.id) { setExpandedId(null); setFullEntry(null); return; }
    setExpandedId(entry.id); setFullLoading(true);
    try {
      const r = await axios.get(`${API}/api/security/entries/${entry.id}/full`, auth());
      setFullEntry(r.data);
    } catch { setFullEntry(entry); }
    setFullLoading(false);
  };

  const handleRequestEdit = async () => {
    if (!editModal) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/security/entries/${editModal.id}/request-edit`, {
        proposed: editForm, reason: editReason
      }, auth());
      setActionMsg("Edit request sent to Review Required for admin approval.");
      setEditModal(null); setEditForm({}); setEditReason("");
      fetchEntries();
    } catch (e) { setError(formatError(e)); }
    setActionLoading(false);
  };

  const handleRequestDelete = async () => {
    if (!deleteModal) return;
    setActionLoading(true);
    try {
      if (isAdmin) {
        // Admin: direct delete, no review needed
        await axios.delete(`${API}/api/security/entries/${deleteModal.id}`, auth());
        setActionMsg("Entry deleted successfully.");
      } else {
        // Non-admin: request goes to Review Required
        await axios.post(`${API}/api/security/entries/${deleteModal.id}/request-delete`, {
          reason: deleteReason
        }, auth());
        setActionMsg("Delete request sent to Review Required for admin approval.");
      }
      setDeleteModal(null); setDeleteReason("");
      fetchEntries();
    } catch (e) { setError(formatError(e)); }
    setActionLoading(false);
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { limit: 300 };
      if (activeCategory) params.entry_category = activeCategory;
      if (selectedKitchen) params.kitchen_id = selectedKitchen;
      if (fromDate) params.date_from = fromDate;
      if (toDate) params.date_to = toDate;
      const res = await securityAPI.getEntries(params);
      setEntries(res.data || []);
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  }, [activeCategory, selectedKitchen, fromDate, toDate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleVisitorExit = async (entryId) => {
    setExitLoading(entryId);
    try {
      await securityAPI.logVisitorExit(entryId);
      setExitDone(prev => ({ ...prev, [entryId]: new Date().toISOString() }));
      fetchEntries();
    } catch (e) { setError(formatError(e)); }
    setExitLoading(null);
  };

  // Counts per category for badge
  const counts = entries.reduce((acc, e) => {
    acc[e.entry_category] = (acc[e.entry_category] || 0) + 1;
    acc[""] = (acc[""] || 0) + 1;
    return acc;
  }, {});

  const filtered = useMemo(() => entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      e.vehicle_number?.toLowerCase().includes(s) ||
      e.route_number?.toString().includes(s) ||
      e.notes?.toLowerCase().includes(s) ||
      e.visitor_name?.toLowerCase().includes(s) ||
      e.visitor_purpose?.toLowerCase().includes(s) ||
      e.created_by_name?.toLowerCase().includes(s)
    );
  }), [entries, search]);

  const handleExport = () => {
    exportCSV(filtered.map(e => ({
      date_time: fmtDateTimeLong(e.entry_time),
      type: e.entry_category?.replace("_", " "),
      direction: e.direction,
      vehicle_route: e.vehicle_number || (e.route_number ? `Route ${e.route_number}` : ""),
      visitor_name: e.visitor_name || "",
      visitor_purpose: e.visitor_purpose || "",
      exit_time: e.exit_time ? fmtDateTimeLong(e.exit_time) : "",
      notes: e.notes || "",
      logged_by: e.created_by_name || "",
    })), `gate_log_${fromDate}_${toDate}`);
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="gate-log">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Gate Log</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">All movements — Food Distribution, Stores, Visitors & Others</p>
        </div>
        <button onClick={handleExport} disabled={filtered.length === 0}
          className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] disabled:opacity-40">
          <Download size={14} /> Export
        </button>
      </div>

      {/* Category filter tabs — hidden for food-distribution-only users */}
      {!foodDistOnly && (
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all border-2 ${activeCategory === cat.id
                ? "border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]"
                : "border-[#E5E1DB] text-[#5C5147] hover:border-[#E8956D]/50 bg-white"}`}
              data-testid={`cat-filter-${cat.id || "all"}`}
            >
              {cat.icon && <cat.icon size={13} />}
              {cat.label}
              {counts[cat.id] > 0 && (
                <span className={`ml-1 text-xs font-extrabold px-1.5 py-0.5 rounded-full ${activeCategory === cat.id ? "bg-[#E8956D] text-white" : "bg-[#F4F3F0] text-[#8A7E73]"}`}>
                  {counts[cat.id]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {foodDistOnly && (
        <div className="flex items-center gap-2 bg-[#EDF2EE] border border-[#637C64]/20 rounded-xl px-4 py-2.5">
          <Truck size={14} className="text-[#637C64]" />
          <span className="text-sm font-medium text-[#637C64]">Showing Food Distribution entries only</span>
        </div>
      )}

      {/* Search + Date filter */}
      <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-48 relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
          <input
            placeholder="Search vehicle, route, visitor, notes..."
            className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">From</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">To</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
        <button onClick={fetchEntries} disabled={loading}
          className="flex items-center gap-2 bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}
      {actionMsg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm">{actionMsg}</div>}

      {/* Log Table */}
      {loading ? (
        <div className="text-center py-16"><RefreshCw size={24} className="animate-spin text-[#E8956D] mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E1DB] rounded-2xl text-[#8A7E73]">
          <Clock size={40} className="mx-auto mb-3 opacity-20" />
          <p className="font-bold text-[#3A322C]">No entries found</p>
          <p className="text-sm mt-1">Try adjusting the date range or category filter</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between">
            <p className="text-sm font-bold text-[#3A322C]">{filtered.length} entries</p>
            <p className="text-xs text-[#8A7E73]">Click any row to expand details</p>
          </div>
          <div className="divide-y divide-[#F4F3F0]">
            {filtered.map((entry, i) => {
              const isVisitor = entry.entry_category === "visitor";
              const hasExited = !!entry.exit_time || !!exitDone[entry.id];
              const exitTime = entry.exit_time ? new Date(entry.exit_time) : (exitDone[entry.id] ? new Date(exitDone[entry.id]) : null);
              const kitchenName = kitchens.find(k => k.id === entry.kitchen_id)?.name;
              const isExpanded = expandedId === entry.id;
              const displayEntry = isExpanded && fullEntry?.id === entry.id ? fullEntry : entry;
              const isPending = entry.edit_pending || entry.delete_pending;

              return (
                <div key={entry.id || i}>
                  {/* Main row — clickable to expand */}
                  <div
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8] transition-colors ${isExpanded ? "bg-[#FAFAF8]" : ""} ${isPending ? "border-l-4 border-l-[#D98032]" : ""}`}
                    onClick={() => toggleExpand(entry)}
                  >
                    <span className="text-[#C4BAAF] shrink-0">
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>

                    {/* Date */}
                    <div className="w-32 shrink-0">
                      <p className="text-xs text-[#8A7E73] whitespace-nowrap">{fmtDateTime(entry.entry_time)}</p>
                      {kitchenName && <p className="text-[10px] text-[#C4BAAF]">{kitchenName}</p>}
                      {isVisitor && entry.direction === "inward" && hasExited && (
                        <p className="text-[10px] text-purple-500 font-bold">
                          Exit: {fmtTime(entry.exit_time || exitDone[entry.id])}
                        </p>
                      )}
                    </div>

                    {/* Type + Direction */}
                    <div className="flex gap-1.5 shrink-0">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CAT_STYLE[entry.entry_category] || "bg-gray-100 text-gray-600"}`}>
                        {entry.entry_category?.replace("_", " ")}
                      </span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${DIR_STYLE[entry.direction] || "bg-gray-100 text-gray-600"}`}>
                        {entry.direction}
                      </span>
                    </div>

                    {/* Details summary */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-2 items-center text-xs">
                        {entry.vendor_name && <span className="font-medium text-[#3A322C]">{entry.vendor_name}</span>}
                        {entry.invoice_number && <span className="font-mono text-[#8A7E73]">#{entry.invoice_number}</span>}
                        {entry.vehicle_number && <span className="font-mono bg-[#F4F3F0] px-1.5 py-0.5 rounded text-[#5C5147]">{entry.vehicle_number}</span>}
                        {entry.route_number && <span className="font-bold text-[#E8956D]">Route {entry.route_number}</span>}
                        {entry.visitor_name && <span className="font-medium text-[#3A322C]">{entry.visitor_name}</span>}
                        {entry.visitor_purpose && <span className="text-[#8A7E73]">{entry.visitor_purpose}</span>}
                        {entry.bill_weight && <span className="text-[#5C5147]">Bill: {entry.bill_weight}kg</span>}
                        {entry.actual_weight && <span className={`font-bold ${parseFloat(entry.actual_weight) < parseFloat(entry.bill_weight || 0) ? "text-[#C2624E]" : "text-[#637C64]"}`}>Actual: {entry.actual_weight}kg</span>}
                      </div>
                    </div>

                    {/* Logged by + status badges */}
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-[#8A7E73]">{entry.created_by_name}</p>
                      <div className="flex gap-1 mt-0.5 justify-end">
                        {entry.edit_pending && <span className="text-[9px] bg-[#FDF4EB] text-[#D98032] px-1.5 rounded font-bold">Edit Pending</span>}
                        {entry.delete_pending && <span className="text-[9px] bg-[#FBEAE7] text-[#C2624E] px-1.5 rounded font-bold">Delete Pending</span>}
                      </div>
                    </div>

                    {/* Actions (stop propagation to avoid expanding) */}
                    <div className="flex gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      {/* Show Exit button only for inward visitor entries that haven't exited yet */}
                      {isVisitor && entry.direction === "inward" && !hasExited && (
                        <button onClick={() => handleVisitorExit(entry.id)} disabled={exitLoading === entry.id}
                          className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded-lg font-bold flex items-center gap-0.5">
                          <LogOut size={10} /> Exit
                        </button>
                      )}
                      {/* Badge for auto-generated exit entries */}
                      {isVisitor && entry.direction === "outward" && entry.linked_inward_id && (
                        <span className="text-[9px] bg-purple-50 text-purple-500 px-1.5 py-0.5 rounded font-bold border border-purple-200">
                          Auto-Exit
                        </span>
                      )}
                      <button onClick={() => { setEditModal(entry); setEditForm({ vendor_name: entry.vendor_name, invoice_number: entry.invoice_number, invoice_date: entry.invoice_date, bill_weight: entry.bill_weight, actual_weight: entry.actual_weight, notes: entry.notes, vehicle_number: entry.vehicle_number }); setEditReason(""); }}
                        className="text-[#8A7E73] hover:text-[#E8956D] p-1 rounded hover:bg-[#F4F3F0]" title="Request edit">
                        <Edit size={13} />
                      </button>
                      <button onClick={() => { setDeleteModal(entry); setDeleteReason(""); }}
                        className="text-[#8A7E73] hover:text-[#C2624E] p-1 rounded hover:bg-[#F4F3F0]" title="Request deletion">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div className="bg-[#FAFAF8] border-t border-[#F4F3F0] px-8 py-4">
                      {fullLoading ? (
                        <p className="text-xs text-[#8A7E73] flex items-center gap-2"><RefreshCw size={12} className="animate-spin" /> Loading full details...</p>
                      ) : (
                        <div className="space-y-3">
                          {/* All fields grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                              ["Vendor", displayEntry.vendor_name],
                              ["Invoice No.", displayEntry.invoice_number],
                              ["Invoice Date", displayEntry.invoice_date],
                              ["Bill Weight", displayEntry.bill_weight ? `${displayEntry.bill_weight} kg` : null],
                              ["Actual Weight", displayEntry.actual_weight ? `${displayEntry.actual_weight} kg` : null],
                              ["Vehicle", displayEntry.vehicle_number],
                              ["Route", displayEntry.route_number ? `Route ${displayEntry.route_number}` : null],
                              ["Notes", displayEntry.notes],
                              ["Visitor", displayEntry.visitor_name],
                              ["Phone", displayEntry.visitor_phone],
                              ["Organisation", displayEntry.visitor_organisation],
                              ["Purpose", displayEntry.visitor_purpose],
                              ["ID Proof", displayEntry.id_proof_type],
                              ["Verified by", displayEntry.verified_by],
                              ["Logged by", displayEntry.created_by_name],
                              ["Exit Time", displayEntry.exit_time ? fmtDateTime(displayEntry.exit_time) : null],
                              ["Exit Recorded By", displayEntry.exit_recorded_by],
                            ].filter(([, v]) => v).map(([label, value]) => (
                              <div key={label}>
                                <p className="text-[10px] font-bold uppercase text-[#8A7E73]">{label}</p>
                                <p className="text-sm text-[#3A322C] font-medium">{value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Photos */}
                          {(displayEntry.photo_url || displayEntry.invoice_photo_url || displayEntry.weight_photo_url) && (
                            <div>
                              <p className="text-[10px] font-bold uppercase text-[#8A7E73] mb-2">Photos</p>
                              <div className="flex gap-3 flex-wrap">
                                {[["Gate Photo", displayEntry.photo_url], ["Invoice", displayEntry.invoice_photo_url], ["Weight", displayEntry.weight_photo_url]].filter(([, url]) => url).map(([label, url]) => (
                                  <div key={label} className="text-center">
                                    <p className="text-[10px] text-[#8A7E73] mb-1">{label}</p>
                                    <img src={url} alt={label} className="w-24 h-24 object-cover rounded-lg border border-[#E5E1DB] cursor-pointer hover:scale-105 transition-transform"
                                      onClick={() => setLightbox({ url, label })} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Vessel counts */}
                          {displayEntry.vessel_counts && Object.values(displayEntry.vessel_counts).some(v => v > 0) && (
                            <div>
                              <p className="text-[10px] font-bold uppercase text-[#8A7E73] mb-1">Vessels</p>
                              <div className="flex gap-2 flex-wrap">
                                {Object.entries(displayEntry.vessel_counts).filter(([, v]) => v > 0).map(([k, v]) => (
                                  <span key={k} className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded">{k.replace("count_","")}: <strong>{v}</strong></span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
              <div>
                <h3 className="font-bold text-[#3A322C]">Request Edit</h3>
                <p className="text-xs text-[#8A7E73] mt-0.5">Changes will go to <strong>Review Required</strong> for admin approval</p>
              </div>
              <button onClick={() => setEditModal(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {[
                { key: "vendor_name", label: "Vendor Name" },
                { key: "invoice_number", label: "Invoice Number" },
                { key: "invoice_date", label: "Invoice Date", type: "date" },
                { key: "bill_weight", label: "Bill Weight (kg)", type: "number" },
                { key: "actual_weight", label: "Actual Weight (kg)", type: "number" },
                { key: "vehicle_number", label: "Vehicle Number" },
                { key: "notes", label: "Notes" },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">{f.label}</label>
                  <input type={f.type || "text"} step={f.type === "number" ? "0.01" : undefined}
                    className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={editForm[f.key] || ""}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason for Edit *</label>
                <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none"
                  placeholder="Why is this change needed?" value={editReason} onChange={e => setEditReason(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5 sticky bottom-0 bg-white pt-3 border-t border-[#E5E1DB]">
              <button onClick={() => setEditModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleRequestEdit} disabled={actionLoading || !editReason.trim()}
                className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2.5 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
                {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <Edit size={14} />}
                Submit for Review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-bold text-[#3A322C]">{isAdmin ? "Delete Entry" : "Request Deletion"}</h3>

            {isAdmin ? (
              <div className="bg-[#FBEAE7] border border-[#C2624E]/20 rounded-xl p-3 text-sm text-[#C2624E]">
                This entry will be <strong>permanently deleted immediately</strong>. A record will be kept in the Deletion Log with your name.
              </div>
            ) : (
              <div className="bg-[#FDF4EB] border border-[#D98032]/20 rounded-xl p-3 text-sm text-[#D98032]">
                This will be sent to <strong>Review Required</strong> for admin approval — it won't be deleted immediately.
              </div>
            )}

            <div className="bg-[#F4F3F0] rounded-xl p-3 text-xs space-y-1">
              <p className="font-bold text-[#3A322C] mb-1">Entry details:</p>
              <p><span className="text-[#8A7E73]">Type:</span> {deleteModal.entry_category?.replace("_"," ")}</p>
              <p><span className="text-[#8A7E73]">Date:</span> {fmtDateTimeLong(deleteModal.entry_time)}</p>
              {deleteModal.vendor_name && <p><span className="text-[#8A7E73]">Vendor:</span> {deleteModal.vendor_name}</p>}
              {deleteModal.invoice_number && <p><span className="text-[#8A7E73]">Invoice:</span> {deleteModal.invoice_number}</p>}
              {deleteModal.visitor_name && <p><span className="text-[#8A7E73]">Visitor:</span> {deleteModal.visitor_name}</p>}
            </div>

            {/* Reason only required for non-admins */}
            {!isAdmin && (
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason for Deletion *</label>
                <textarea rows={3} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                  placeholder="e.g. Duplicate entry, wrong record..." value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setDeleteModal(null); setDeleteReason(""); }} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleRequestDelete}
                disabled={actionLoading || (!isAdmin && !deleteReason.trim())}
                className="flex-1 bg-[#C2624E] text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
                {actionLoading ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {isAdmin ? "Delete Permanently" : "Send for Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PHOTO LIGHTBOX ── */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-white font-bold text-sm">{lightbox.label}</p>
              <button onClick={() => setLightbox(null)} className="text-white/70 hover:text-white bg-white/10 rounded-full p-2">
                <X size={18} />
              </button>
            </div>
            {/* Full image */}
            <img
              src={lightbox.url}
              alt={lightbox.label}
              className="w-full max-h-[80vh] object-contain rounded-xl"
              style={{ background: "#111" }}
            />
            <p className="text-center text-white/40 text-xs mt-2">Tap outside to close</p>
          </div>
        </div>
      )}
    </div>
  );
}
